import { get, API_URL } from '../utils/api';

/**
 * Gets account information by site and unit
 * @param {string} site - Site name
 * @param {number|string} unit - Unit ID or number
 * @param {string} [siteUuid] - Optional site UUID (to skip lookup)
 * @param {string} [unitUuid] - Optional unit UUID (to skip lookup)
 * @returns {Promise<object|null>} - Account information or null if error
 */
export const getAccountByUnit = async (site, unit, siteUuid = null, unitUuid = null) => {
  try {
    // console.log(`Fetching account data for site: ${site}, unit: ${unit}`);
    const params = { site, unit };
    
    // Add UUIDs if they're available to help backend optimize lookups
    if (siteUuid) params.siteUuid = siteUuid;
    if (unitUuid) params.unitUuid = unitUuid;
    
    const response = await get(`${API_URL}/account/by-unit`, params);
    
    return response.data;
  } catch (error) {
    // Don't log 404 errors as they're expected when services aren't available
    if (error.response?.status !== 404) {
      // console.error('Error fetching account by unit:', error.response?.data || error.message);
    }
    return null;
  }
};

/**
 * Gets unit information by site UUID and unit ID
 * @param {string} siteUuid - UUID of the site
 * @param {number|string} unitId - ID of the unit
 * @returns {Promise<object|null>} - Unit information or null if error
 */
export const getUnitByUnitId = async (siteUuid, unitId) => {
  try {
    // console.log(`Fetching unit data for siteUuid: ${siteUuid}, unitId: ${unitId}`);
    const response = await get(`${API_URL}/site/units/byUnitId`, { siteUuid, unitId });
    
    return response.data;
  } catch (error) {
    // Don't log 404 errors as they're expected when services aren't available
    if (error.response?.status !== 404) {
      // console.error('Error fetching unit by unitId:', error.response?.data || error.message);
    }
    return null;
  }
};

/**
 * Gets unit information by site name and/or unit ID 
 * @param {string} siteName - Name of the site
 * @param {number|string} unitId - ID of the unit
 * @param {string} [siteUuid] - Optional UUID of the site (to avoid lookup)
 * @returns {Promise<object|null>} - Unit information or null if error
 */
export const getUnitByNameAndId = async (siteName, unitId, siteUuid = null) => {
  try {
    // console.log(`Fetching unit data for site: ${siteName}, unitId: ${unitId}`);
    
    const params = { siteName, unitId };
    if (siteUuid) params.siteUuid = siteUuid;
    
    // Use the byUnitId endpoint with siteName instead since by-name-id doesn't exist
    const response = await get(`${API_URL}/site/units/byUnitId`, params);
    
    // Enhanced logging for debugging
    // console.log(`Found unit data:`, response.data);
    // console.log(`Unit isPrepaid value:`, response.data.isPrepaid);
    // console.log(`Unit isPrepaid type:`, typeof response.data.isPrepaid);
    
    return response.data;
    
  } catch (error) {
    // console.error('Error fetching unit by name and ID:', error.response?.data || error.message);
    return null;
  }
}; 